import React, { useState, useEffect } from 'react';
import TeamMemberService from '../services/TeamMemberService';

const ProviderInfo: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<any[]>([]);

  useEffect(() => {
    // Get service instance
    const teamMemberService = TeamMemberService.getInstance();

    // Load initial data
    const loadData = () => {
      const members = teamMemberService.getAllTeamMembers();
      console.log('ProviderInfo: Loaded team members:', members);
      setTeamMembers(members);
    };

    loadData();

    // Listen for data changes
    const handleDataUpdate = () => {
      console.log('ProviderInfo: Team members updated, reloading...');
      loadData();
    };

    // Subscribe to updates
    teamMemberService.subscribe(handleDataUpdate);

    return () => {
      teamMemberService.unsubscribe(handleDataUpdate);
    };
  }, []);

  return (
    <div style={{
      maxWidth: 1200,
      margin: '2rem auto',
      padding: '0 1rem',
      color: '#1e293b'
    }}>
      <h1 style={{
        textAlign: 'center',
        marginBottom: '3rem',
        color: '#3b82f6',
        fontSize: '2.5rem',
        fontWeight: 700
      }}>
        Our Healthcare Providers
      </h1>

      {teamMembers.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#d1d5db',
          fontSize: '1.1rem'
        }}>
          No providers have been added yet.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '3rem' }}>
          {teamMembers.map((provider) => (
            <div key={provider.id} style={{
              background: '#fff',
              borderRadius: 16,
              boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
              padding: '2.5rem 2rem',
              border: '3px solid #6CA6C1'
            }}>
              <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Provider Photo */}
                <div style={{
                  minWidth: 160,
                  minHeight: 160,
                  width: 160,
                  height: 160,
                  background: '#f3f4f6',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 48,
                  color: '#d1d5db',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  border: '3px solid #6CA6C1'
                }}>
                  {provider.photoUrl ? (
                    <img src={provider.photoUrl} alt={provider.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span>👩‍⚕️</span>
                  )}
                </div>

                {/* Provider Details */}
                <div style={{ flex: 1 }}>
                  <h2 style={{ fontSize: '2rem', margin: 0, color: '#1e293b', fontWeight: 700 }}>{provider.name}</h2>
                  <div style={{ color: '#3b82f6', fontWeight: 600, fontSize: '1.1rem', marginBottom: 8 }}>{provider.role}</div>
                  {provider.bio && <div style={{ color: '#d1d5db', marginBottom: 16 }}>{provider.bio}</div>}
                  <div style={{ marginBottom: 8 }}><strong>Email:</strong> {provider.email}</div>
                  {provider.availability && <div style={{ marginBottom: 8 }}><strong>Availability:</strong> {provider.availability}</div>}
                  {provider.languages && provider.languages.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <strong>Languages:</strong> {provider.languages.join(', ')}
                      {provider.languageOther && provider.languageOtherText && `, ${provider.languageOtherText}`}
                    </div>
                  )}
                </div>
              </div>

              {/* Education */}
              {provider.education && provider.education.length > 0 && (
                <div style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                  <h3 style={{
                    marginBottom: '1rem',
                    color: '#ffffff',
                    fontSize: '1.5rem',
                    fontWeight: 600
                  }}>Education</h3>
                  <div style={{
                    padding: '1.5rem',
                    background: '#4a5568',
                    borderRadius: '12px',
                    color: '#ffffff'
                  }}>
                    {Array.isArray(provider.education) ? (
                      <ul style={{ margin: 0, paddingLeft: '1.2rem', listStyle: 'disc' }}>
                        {provider.education.map((item: string, index: number) => (
                          <li key={index} style={{ marginBottom: '0.5rem', color: '#ffffff' }}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      provider.education
                    )}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {provider.certifications && provider.certifications.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{
                    marginBottom: '1rem',
                    color: '#ffffff',
                    fontSize: '1.5rem',
                    fontWeight: 600
                  }}>Certifications</h3>
                  <div style={{
                    padding: '1.5rem',
                    background: '#4a5568',
                    borderRadius: '12px',
                    color: '#ffffff'
                  }}>
                    {Array.isArray(provider.certifications) ? (
                      <ul style={{ margin: 0, paddingLeft: '1.2rem', listStyle: 'disc' }}>
                        {provider.certifications.map((item: string, index: number) => (
                          <li key={index} style={{ marginBottom: '0.5rem', color: '#ffffff' }}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      provider.certifications
                    )}
                  </div>
                </div>
              )}

              {/* Specialties */}
              {provider.specialties && provider.specialties.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ marginBottom: '1rem', color: '#3b82f6' }}>Specialties</h3>
                  <div style={{
                    padding: '1rem',
                    background: '#f3f4f6',
                    borderRadius: '4px',
                    color: '#374151',
                    fontWeight: 500
                  }}>
                    {Array.isArray(provider.specialties) ? provider.specialties.join(', ') : provider.specialties}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProviderInfo;